import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { Client, Invoice, Organization } from '@repo/api-client/dtos';
import { DEFAULT_TEMPLATE_ID } from '@repo/api-client';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { TemplateCanvas } from './TemplateCanvas';
import { PreviewCanvasFrame } from './PreviewCanvasFrame';
import { buildArtboardPayload } from './buildArtboardPayload';
import {
  A4_HEIGHT_PX,
  A4_WIDTH_PX,
  previewCanvasScale,
} from './previewCanvasConstants';

export interface FrozenArtifactPreviewProps {
  invoice: Invoice;
  organization: Organization;
  client: Client | null;
  width: number;
  className?: string;
  /** Resolved preview JPG URL (finalDocument or printer cache). */
  previewUrl: string | null;
  /** When the durable send pipeline is still rendering. */
  documentStatus?: 'rendering' | 'ready' | 'failed' | null;
  documentError?: string | null;
  /** True while we are waiting for a printer artefact (no URL yet). */
  waitingForArtifact?: boolean;
  onRetry?: () => void;
}

/**
 * Read-only preview for issued invoices. Prefers the frozen JPG artefact;
 * falls back to snapshot-only TemplateCanvas when no image is available yet.
 */
export function FrozenArtifactPreview({
  invoice,
  organization,
  client,
  width,
  className,
  previewUrl,
  documentStatus,
  documentError,
  waitingForArtifact,
  onRetry,
}: FrozenArtifactPreviewProps) {
  const scale = previewCanvasScale(width);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
  }, [previewUrl]);

  const snapshotPayload = useMemo(
    () =>
      buildArtboardPayload({
        invoice,
        organization,
        client,
        templateId: invoice.templateId ?? DEFAULT_TEMPLATE_ID,
      }),
    [invoice, organization, client],
  );

  if (documentStatus === 'failed') {
    return (
      <Alert variant="danger" className="max-w-lg">
        <p className="text-sm font-medium">Could not prepare the issued copy</p>
        {documentError ? (
          <p className="mt-1 text-sm text-muted-foreground">{documentError}</p>
        ) : null}
        {onRetry ? (
          <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
      </Alert>
    );
  }

  if (previewUrl) {
    return (
      <PreviewCanvasFrame width={width} className={className}>
        {!imageLoaded ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        ) : null}
        <img
          src={previewUrl}
          alt={`Invoice ${invoice.invoiceNumber} preview`}
          ref={(node) => {
            if (node?.complete && node.naturalWidth > 0) {
              setImageLoaded(true);
            }
          }}
          onLoad={() => setImageLoaded(true)}
          className="block max-w-none bg-white"
          style={{
            width: A4_WIDTH_PX,
            height: A4_HEIGHT_PX,
            transform: `scale(${scale})`,
            transformOrigin: '0 0',
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 150ms ease-out',
          }}
        />
      </PreviewCanvasFrame>
    );
  }

  if (
    documentStatus === 'rendering' ||
    waitingForArtifact
  ) {
    return (
      <div
        className="flex items-center justify-center rounded-md bg-white text-sm text-muted-foreground ring-1 ring-line"
        style={{
          width: A4_WIDTH_PX * scale,
          height: A4_HEIGHT_PX * scale,
        }}
      >
        <Loader2 className="mr-2 size-4 animate-spin" />
        Preparing issued copy…
      </div>
    );
  }

  return (
    <TemplateCanvas
      payload={snapshotPayload}
      templateId={invoice.templateId ?? DEFAULT_TEMPLATE_ID}
      width={width}
      className={className}
    />
  );
}
